import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Mail, Phone, RefreshCw, Inbox } from "lucide-react";
import { api, clearToken, getToken } from "../../lib/auth";
import logoWhite from "../../assets/logo-white-transparent.png";

const STATUSES = ["new", "contacted", "enrolled", "archived"];
const STATUS_COLOR = {
  new: { bg: "#FDECEC", fg: "#C1121F" },
  contacted: { bg: "#FFF3DE", fg: "#B4791A" },
  enrolled: { bg: "#E6F4EA", fg: "#2E7D32" },
  archived: { bg: "#ECECEC", fg: "#6B6B6B" },
};

export const AdminDashboard = () => {
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => { clearToken(); nav("/admin/login"); }, [nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([api.get("/inquiries"), api.get("/inquiries/stats")]);
      setLeads(l.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) { toast.error("Session expired. Please sign in."); logout(); }
      else toast.error("Failed to load leads.");
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (!getToken()) { nav("/admin/login"); return; }
    load();
  }, [load, nav]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/inquiries/${id}`, { status });
      setLeads((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
      load();
      toast.success(`Marked as ${status}`);
    } catch { toast.error("Update failed."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/inquiries/${id}`);
      setLeads((prev) => prev.filter((x) => x.id !== id));
      load();
      toast.success("Lead deleted.");
    } catch { toast.error("Delete failed."); }
  };

  const shown = filter === "all" ? leads : leads.filter((l) => l.status === filter);
  const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

  const cards = [
    { label: "Total Leads", value: stats.total },
    { label: "New", value: stats.by_status?.new || 0 },
    { label: "Contacted", value: stats.by_status?.contacted || 0 },
    { label: "Enrolled", value: stats.by_status?.enrolled || 0 },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-alt)" }} data-testid="admin-dashboard">
      <header style={{ background: "var(--black)", padding: "18px 0" }}>
        <div className="container" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src={logoWhite} alt="VLA" style={{ width: 40, height: 40, objectFit: "contain" }} />
            <span style={{ color: "#fff", fontFamily: "'Bebas Neue'", fontSize: 24, letterSpacing: "0.08em" }}>VLA · Leads</span>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <button className="btn btn-outline" data-testid="refresh-btn" onClick={load}
              style={{ padding: "10px 18px", fontSize: 12 }}><RefreshCw size={15} /> Refresh</button>
            <button className="btn btn-gold" data-testid="logout-btn" onClick={logout}
              style={{ padding: "10px 18px", fontSize: 12 }}><LogOut size={15} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="container" style={{ padding: "36px 24px 80px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 20, marginBottom: 30 }} className="admin-cards">
          {cards.map((c) => (
            <div key={c.label} data-testid={`stat-${c.label}`}
              style={{ background: "#fff", borderRadius: 16, padding: "24px 26px", boxShadow: "var(--shadow-soft)",
                borderTop: "3px solid var(--gold)" }}>
              <div style={{ fontFamily: "'Bebas Neue'", fontSize: 44, color: "var(--black)", lineHeight: 1 }}>{c.value}</div>
              <div style={{ fontSize: 12.5, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted)", marginTop: 4 }}>{c.label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 10, marginBottom: 22, flexWrap: "wrap" }}>
          {["all", ...STATUSES].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
              style={{ padding: "9px 18px", borderRadius: 100, fontSize: 12.5, fontWeight: 700, textTransform: "uppercase",
                letterSpacing: "0.05em", border: "1px solid rgba(0,0,0,0.1)",
                background: filter === s ? "var(--black)" : "#fff", color: filter === s ? "#fff" : "var(--muted)" }}>
              {s}
            </button>
          ))}
        </div>

        {loading ? (
          <p style={{ color: "var(--muted)", padding: 40, textAlign: "center" }}>Loading leads…</p>
        ) : shown.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "var(--muted)" }}>
            <Inbox size={40} style={{ opacity: 0.4, margin: "0 auto 12px" }} />
            <p>No leads {filter !== "all" ? `with status "${filter}"` : "yet"}.</p>
          </div>
        ) : (
          <div style={{ display: "grid", gap: 14 }} data-testid="leads-list">
            {shown.map((l) => {
              const sc = STATUS_COLOR[l.status] || STATUS_COLOR.new;
              return (
                <div key={l.id} data-testid={`lead-row-${l.id}`}
                  style={{ background: "#fff", borderRadius: 16, padding: "22px 24px", boxShadow: "var(--shadow-soft)",
                    display: "grid", gridTemplateColumns: "1.4fr 1.6fr auto", gap: 20, alignItems: "center" }}
                  className="lead-row">
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <strong style={{ fontSize: 16.5, color: "var(--black)" }}>{l.name}</strong>
                      <span style={{ background: sc.bg, color: sc.fg, fontSize: 11, fontWeight: 800, textTransform: "uppercase",
                        letterSpacing: "0.06em", padding: "3px 10px", borderRadius: 100 }}>{l.status}</span>
                    </div>
                    <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 6 }}>
                      {l.program || "General"} · {fmt(l.created_at)}
                    </div>
                    <div style={{ display: "flex", gap: 16, marginTop: 8, flexWrap: "wrap" }}>
                      <a href={`mailto:${l.email}`} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13, color: "var(--red)" }}><Mail size={14} /> {l.email}</a>
                      <a href={`tel:${l.phone}`} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13, color: "var(--red)" }}><Phone size={14} /> {l.phone}</a>
                    </div>
                  </div>
                  <p style={{ fontSize: 14, color: "var(--text)", borderLeft: "3px solid var(--bg-alt)", paddingLeft: 14 }}>
                    {l.message || <span style={{ color: "#b0b0ab" }}>No message</span>}
                  </p>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <select value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)} data-testid={`status-select-${l.id}`}
                      style={{ padding: "9px 12px", borderRadius: 10, border: "1.5px solid rgba(0,0,0,0.12)", fontSize: 13, background: "#fff" }}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button onClick={() => remove(l.id)} data-testid={`delete-${l.id}`} aria-label="Delete lead"
                      style={{ width: 40, height: 40, borderRadius: 10, background: "#FDECEC", color: "#C1121F",
                        display: "flex", alignItems: "center", justifyContent: "center" }}><Trash2 size={17} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
